import React from 'react';
import { useSelector } from 'react-redux';
import { useAction } from 'src/hooks/useAction';
import { useBoundAction } from 'src/hooks/useBoundAction';
import { Modal } from 'src/modules/common/components/Modal';
import { PreserveContent } from 'src/modules/common/components/PreserveContent';
import { getLanguage } from 'src/modules/config/selectors/getLanguage';
import { getLoggedInUser } from 'src/modules/login/selectors/getLoggedInUser';
import {
  SHARE_PROJECT_CLOSE,
  SHARE_PROJECT_SUBMIT,
  SHARE_PROJECT_REMOVE_CLOSE,
  SHARE_PROJECT_REMOVE_OPEN,
  SHARE_PROJECT_REMOVE_SUBMIT,
  SHARE_PROJECT_CHANGE_ROLE,
  SHARE_PROJECT_CHANGE_OWN_ROLE_CLOSE,
  SHARE_PROJECT_CHANGE_OWN_ROLE_OPEN,
  SHARE_PROJECT_CHANGE_OWN_ROLE_SUBMIT,
  SHARE_PROJECT_CHANGE_OWNER_CLOSE,
  SHARE_PROJECT_CHANGE_OWNER_OPEN,
  SHARE_PROJECT_CHANGE_OWNER_SUBMIT,
  SHARE_PROJECT_COPY_LINK,
} from 'src/modules/sharing-projects/actions/ShareProjectActions';
import { getShareProjectData } from 'src/modules/sharing-projects/selectors/getShareProjectData';
import { ShareProjectChangeOwnerModal } from 'src/modules/sharing-projects/views/ShareProjectChangeOwnerModal';
import { ShareProjectChangeOwnRoleModal } from 'src/modules/sharing-projects/views/ShareProjectChangeOwnRoleModal';
import { ShareProjectModal } from 'src/modules/sharing-projects/views/ShareProjectModal';
import { ShareProjectRemoveModal } from 'src/modules/sharing-projects/views/ShareProjectRemoveModal';

export const ShareProject = (): React.ReactElement => {
  const modalData = useSelector(getShareProjectData);
  const loggedInUser = useSelector(getLoggedInUser);

  const language = useSelector(getLanguage);

  const shareProjectClose = useBoundAction(SHARE_PROJECT_CLOSE.trigger, undefined);
  const shareProjectSubmit = useAction(SHARE_PROJECT_SUBMIT.request);

  const removeUserClose = useBoundAction(SHARE_PROJECT_REMOVE_CLOSE.trigger, undefined);
  const removeUserRequest = useAction(SHARE_PROJECT_REMOVE_OPEN.trigger);
  const removeUserConfirm = useAction(SHARE_PROJECT_REMOVE_SUBMIT.request);

  const changeUserRole = useAction(SHARE_PROJECT_CHANGE_ROLE.request);

  const changeOwnRoleClose = useBoundAction(SHARE_PROJECT_CHANGE_OWN_ROLE_CLOSE.trigger);
  const changeOwnRoleOpen = useAction(SHARE_PROJECT_CHANGE_OWN_ROLE_OPEN.trigger);
  const changeOwnRoleSubmit = useAction(SHARE_PROJECT_CHANGE_OWN_ROLE_SUBMIT.request);

  const changeProjectOwnerClose = useBoundAction(SHARE_PROJECT_CHANGE_OWNER_CLOSE.trigger);
  const changeProjectOwnerOpen = useAction(SHARE_PROJECT_CHANGE_OWNER_OPEN.trigger);
  const changeProjectOwnerSubmit = useAction(SHARE_PROJECT_CHANGE_OWNER_SUBMIT.request);

  const copyLinkToProject = useAction(SHARE_PROJECT_COPY_LINK.trigger);

  return (
    <>
      <Modal
        opened={modalData !== null}
        handleClose={shareProjectClose}
        size="sm"
        shouldCloseOnOverlayClick={true}
      >
        <PreserveContent>
          {modalData !== null && (
            <ShareProjectModal
              language={language}
              project={modalData.project}
              modalData={modalData}
              loggedInUser={loggedInUser}
              onSubmit={shareProjectSubmit}
              onCancel={shareProjectClose}
              onRemove={removeUserRequest}
              changeUserRole={changeUserRole}
              changeOwnRole={changeOwnRoleOpen}
              changeProjectOwner={changeProjectOwnerOpen}
              copyLinkToProject={copyLinkToProject}
            />
          )}
        </PreserveContent>
      </Modal>

      <Modal
        opened={modalData !== null && modalData.removing !== null}
        handleClose={removeUserClose}
        size="sm"
      >
        <PreserveContent>
          {modalData !== null && modalData.removing !== null && (
            <ShareProjectRemoveModal
              project={modalData.project}
              user={modalData.removing.user}
              submitting={modalData.removing.status?.state === 'pending'}
              onConfirm={removeUserConfirm}
              onCancel={removeUserClose}
            />
          )}
        </PreserveContent>
      </Modal>

      <Modal
        opened={modalData !== null && modalData.changingOwnRole !== null}
        handleClose={changeOwnRoleClose}
        size="sm"
      >
        <PreserveContent>
          {modalData !== null && modalData.changingOwnRole !== null && (
            <ShareProjectChangeOwnRoleModal
              user={modalData.changingOwnRole.user}
              role={modalData.changingOwnRole.role}
              submitting={modalData.changingOwnRole.status?.state === 'pending'}
              onSubmit={changeOwnRoleSubmit}
              onCancel={changeOwnRoleClose}
            />
          )}
        </PreserveContent>
      </Modal>

      <Modal
        opened={modalData !== null && modalData.transferringOwnership !== null}
        handleClose={changeProjectOwnerClose}
        size="sm"
      >
        <PreserveContent>
          {modalData !== null && modalData.transferringOwnership !== null && (
            <ShareProjectChangeOwnerModal
              user={modalData.transferringOwnership.user}
              submitting={modalData.transferringOwnership.status?.state === 'pending'}
              onSubmit={changeProjectOwnerSubmit}
              onCancel={changeProjectOwnerClose}
            />
          )}
        </PreserveContent>
      </Modal>
    </>
  );
};
